import { Box, Flex, Heading, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import type { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import {
  type BidV2Status,
  PortalLifecycleStatus,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { BidV2StatusTag } from "../../../v2/sourcing-event/BidV2StatusTag";
import { PortalLifecycleTag } from "../components/PortalLifecycleTag";
import { getBuyerInitials, getTimeLeftMeta } from "../utils/displayFormatters";
import { effectiveDeadlineIso } from "../utils/effectiveDeadline";

export const EventHero = ({
  event,
  now,
  ownBidStatus,
}: {
  event: PortalSourcingEventDetailDto;
  now: Moment;
  ownBidStatus?: BidV2Status;
}) => {
  const { t } = useTranslation();
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  // Null during the qualification window — there is no active deadline to count down to.
  const deadlineIso = effectiveDeadlineIso(event);
  const timeLeftMeta = deadlineIso != null ? getTimeLeftMeta(deadlineIso, t, now) : null;

  return (
    <Stack spacing="4">
      <Heading as="h1" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" lineHeight="1.2">
        {event.title}
      </Heading>

      <HStack spacing="3" align="center" flexWrap="wrap" rowGap="2">
        <PortalLifecycleTag status={event.lifecycle.status} stage={event.competitionStage} size="sm" />

        {ownBidStatus && (
          <>
            <MetaSeparator />
            <HStack spacing="2">
              <Text fontSize="sm" color="smMuted">
                {t("My bid")}
              </Text>
              <BidV2StatusTag
                status={ownBidStatus}
                awarded={event.lifecycle.status === PortalLifecycleStatus.Awarded}
                size="sm"
              />
            </HStack>
          </>
        )}

        {isOpen && timeLeftMeta != null && (
          <>
            <MetaSeparator />
            <HStack spacing="1.5" color={timeLeftMeta.color}>
              <Icon as={FiClock} boxSize="3.5" color={timeLeftMeta.color} />
              <Text fontSize="sm">{timeLeftMeta.label}</Text>
            </HStack>
          </>
        )}

        <MetaSeparator />

        <HStack spacing="2" color="smMuted">
          <Flex
            width="20px"
            height="20px"
            rounded="md"
            bg="smBackgroundSecondary"
            color="smMuted"
            fontSize="10px"
            fontWeight="bold"
            align="center"
            justify="center"
          >
            {getBuyerInitials(event.buyerName)}
          </Flex>
          <Text fontSize="sm" fontWeight="semibold">
            {event.buyerName}
          </Text>
        </HStack>

        {event.referenceId && (
          <>
            <MetaSeparator />
            <Text fontSize="sm" color="smMuted">
              {event.referenceId}
            </Text>
          </>
        )}
      </HStack>
    </Stack>
  );
};

const MetaSeparator = () => (
  <Box height="14px" width="1px" bg="smBorder" display={{ base: "none", sm: "block" }} />
);
